import { registerLicense } from '@syncfusion/ej2-base';
import {
  SpreadsheetComponent,
} from '@syncfusion/ej2-react-spreadsheet';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { EditorConfig, SavePayload } from '@/types/editor.types';
import { AutosaveIndicator } from '@components/AutoSaveIndicator/AutoSaveIndicator';
import { DocumentTypeBadge } from '@components/DocumentTypeBadge/DocumentTypeBadge';
import { VersionReasonModal } from '@components/VersionReasonModal/VersionReasonModal';
import { environment } from '@config/environment.config';
import { useAutosave } from '@hooks/useAutosave';
import { useEditorCommunication } from '@hooks/useEditorCommunication';
import { useEditorInit } from '@hooks/useEditorInit';
import { useEditorStateMachine } from '@hooks/useEditorStateMachine';
import { useToast } from '@hooks/useToast';
import type { EditorInitData } from '@services/editorApi.service';
import {
  autosaveSpreadsheetFileVersion,
  convertSpreadsheetToCSV,
  exportSpreadsheetAsExcel,
  exportSpreadsheetAsPDF,
  fetchSpreadsheetEditorInit,
  saveAsSpreadsheetFileVersion,
  saveSpreadsheetFileVersion,
} from '@services/editorApi.service';
import styles from './SpreadsheetEditor.module.css';

const SYNCFUSION_LICENSE_KEY = environment.getSyncfusionLicenseKey();
if (SYNCFUSION_LICENSE_KEY) registerLicense(SYNCFUSION_LICENSE_KEY);

const COMM_CONFIG = {
  targetOrigin: '*',
  debug: false,
};
interface LockOperation {
  sheetIndex: number;
  sheetName:  string;
  range:      string;   // plain range e.g. "A1:C3" — no sheet prefix
  locked:     boolean;
}

const LOCK_TINT_COLOR = '#fffde7';

function indexToColumn(colIndex: number): string {
  let col = '';
  let n = colIndex + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    col = String.fromCharCode(65 + rem) + col;
    n = Math.floor((n - 1) / 26);
  }
  return col || 'A';
}

export const SpreadsheetEditor: React.FC = () => {
  const spreadsheetRef = useRef<SpreadsheetComponent>(null);

  // ── Refs (never stale in callbacks) ────────────────────────────────────────
  const canEditRef      = useRef(false);
  const isCreatedRef    = useRef(false);
  const fileMenuConfiguredRef = useRef(false);
  const activateAfterProtectionRef = useRef(false);
  // Mirrors TextEditor's pattern: store pending content so `created` can load it
  const pendingContentRef = useRef<string | null>(null);
  // Guard flag: suppress dirty-state side-effects while programmatically re-applying protection
  const isReprotectingRef = useRef(false);
  const permissionsRef  = useRef({ save: false, saveAs: false, export: false, print: true });
  const allowVersionCreationDisabledRef = useRef(false);
  const hiddenSheetsRef = useRef<Set<number>>(new Set());
  const hiddenSheetNameRef = useRef<Set<string>>(new Set());
  const prevSheetCountRef = useRef<number>(0);
  const lockedRangesRef   = useRef<LockOperation[]>([]);
  const documentTypeRef   = useRef<'regular' | 'master' | 'control' | null>(null);

  // ── State ──────────────────────────────────────────────────────────────────
  const [canEdit, setCanEdit]         = useState(false);
  const [showRibbon, setShowRibbon]   = useState(true);
  const [showFormulaBar, setShowFormulaBar] = useState(true);
  const [documentName, setDocumentName]     = useState('Workbook');
  const [documentType, setDocumentType]     = useState<'regular' | 'master' | 'control' | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lockReason, setLockReason] = useState<string | null>(null);
  // Pending message to inject into the next EditAlertDialog via dialogBeforeOpen
  const pendingAlertMessageRef = useRef<string | null>(null);

  // ── State Machine (shared with TextEditor) ─────────────────────────────────
  const stateMachine = useEditorStateMachine();

  const [lockVersion, setLockVersion]     = useState(0);
  const [activeSheetIdx, setActiveSheetIdx] = useState(0);
  // Version reason modal state
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [pendingVersionReason, setPendingVersionReason] = useState<string | null>(null);
  const handleLockSelectionRef  = useRef<() => void>(() => {});
  const handleUnlockSelectionRef = useRef<() => void>(() => {});
  // ── Save payload ───────────────────────────────────────────────────────────
  const getSpreadsheetJson = useCallback(async (): Promise<string> => {
  const spreadsheet = spreadsheetRef.current;
  if (!spreadsheet) return '';

  try {
    // Commit any in-progress cell edit before serializing —
    // saveAsJson() captures the data model which doesn't include
    // the value currently being typed in the active cell
    (spreadsheet as any).endEdit();
  } catch (_) {
    // endEdit() throws if no edit is in progress — safe to ignore
  }

  try {
    const result = await Promise.race<{ jsonObject: unknown }>([
      (spreadsheet as any).saveAsJson(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('saveAsJson timed out')), 15000)
      ),
    ]);
    try {
      const json = result.jsonObject as any;
      const sheets = json?.Workbook?.sheets ?? json?.sheets ?? [];
      sheets.forEach((sheet: any) => {
        // Preserve isProtected, protectSettings, and password so the full
        // protection state (including user-set passwords) survives save/reload.
        // The password is a document-level credential, not a system credential.
      });
    } catch (_) {}

    return JSON.stringify(result.jsonObject);
  } catch (err) {
    return '';
  }
}, []);

  const buildSavePayload = useCallback(async (): Promise<SavePayload> => {
    let spreadsheetJson = await getSpreadsheetJson();
    // After getting spreadsheetJson, add this block before the return
    if (lockedRangesRef.current.length > 0) {
      try {
        const parsed = JSON.parse(spreadsheetJson);
        parsed._lockedRanges = lockedRangesRef.current;
        spreadsheetJson = JSON.stringify(parsed);
      } catch (_) {}
    }
    return {
      content: { spreadsheetJson },
      comments: { allComments: [], sessionMentions: [] },
      metadata: { documentName, timestamp: Date.now(), componentType: 'spreadsheet' },
    };
  }, [getSpreadsheetJson, documentName]);

  // ── Autosave ───────────────────────────────────────────────────────────────
  const autosave = useAutosave({
    config: stateMachine.config?.autosave,
    isDirty: hasUnsavedChanges,
    canSave: canEdit && stateMachine.state === 'ACTIVE',
    onSave: async () => {
      const id = versionIdRef.current;
      if (!id) return;
      try {
        const payload = await buildSavePayload();
        await autosaveSpreadsheetFileVersion({
          _id: id,
          sfdtData: payload.content.spreadsheetJson || '',
          data: payload.content.spreadsheetJson || '',
        });
        autosave.onSaveComplete();
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error('[SpreadsheetEditor] Autosave failed:', err);
        autosave.onSaveFailed((err as Error).message || 'Autosave failed');
      }
    },
  });

  // ── Activate (drives state machine to ACTIVE) ──────────────────────────────
  const activateEditor = useCallback(() => {
    setHasUnsavedChanges(false);

    const spreadsheet = spreadsheetRef.current;
    if (spreadsheet) {
      const sheetCount = spreadsheet.sheets?.length ?? 1;
        for (let i = 0; i < sheetCount; i++) {
          try {
            if (!canEditRef.current) {
              spreadsheet.protectSheet(i, {
                selectCells: true, formatCells: false,
                formatColumns: false, formatRows: false, insertLink: false,
              });
            } else if (documentTypeRef.current === 'control') {
              applyControlCopySheetProtection(spreadsheet, i);
            } else {
                // Only unprotect sheets that weren't explicitly protected and saved.
                // openFromJson already restored isProtected for those sheets.
                const sheet = (spreadsheet.sheets?.[i] ?? {}) as any;
                if (!sheet.isProtected) {
                  try { (spreadsheet as any).unprotectSheet(i, ''); } catch (_) {}
                }
            }
          } catch (_) {}
        }
        if (canEditRef.current && documentTypeRef.current !== 'control') {
          setTimeout(() => replayTints(spreadsheet), 100);
          setLockVersion(v => v + 1);
        }
    }

    // Transition state machine: LOADING_DOCUMENT → ACTIVE (or READY → ACTIVE)
    stateMachine.onDocumentLoaded();
    syncToolbarVisibility();
    // The state machine may go through APPLYING_PROTECTION if control copy,
    // but spreadsheets don't use that — so it goes straight to ACTIVE.
    // If the machine is now in READY (after APPLYING_PROTECTION→READY),
    // nudge it to ACTIVE.
    if (stateMachine.state === 'READY') {
      stateMachine.onActivateEditor();
    }
  }, [stateMachine]);

  const handleContextMenuBeforeOpen = useCallback((args: any) => {
    if (!args?.items) return;
    args.items = args.items.filter(
      (item: any) => !String(item.text ?? '').toLowerCase().includes('protect')
    );
  }, []);

  const handleLockSelection = useCallback(() => {
    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet || stateMachine.state !== 'ACTIVE') return;

    if (documentTypeRef.current !== 'regular') {
      const label = documentTypeRef.current === 'master' ? 'master copy' : 'control copy';
      pendingAlertMessageRef.current =
        `Cell locking is managed on the ${label}. Individual lock/unlock changes cannot be made here.`;
      try { (spreadsheet as any).notify('editAlert', {}); } catch (_) {}
      return;
    }

    const idx       = (spreadsheet as any).activeSheetIndex ?? 0;
    const sheet     = (spreadsheet.sheets?.[idx] ?? {}) as any;
    const selRange  = (sheet.selectedRange ?? '') as string;
    if (!selRange) return;

    const sheetName: string = sheet.name ?? `Sheet${idx + 1}`;
    lockedRangesRef.current = [
      ...lockedRangesRef.current,
      { sheetIndex: idx, sheetName, range: selRange, locked: true },
    ];

    try {
      (spreadsheet as any).cellFormat(
        { backgroundColor: LOCK_TINT_COLOR },
        `${sheetName}!${selRange}`,
      );
    } catch (_) {}

    setLockVersion(v => v + 1);
    try {
      const count = activeLockCount; // will recalculate on next render
      (spreadsheetRef.current as any)?.updateToolbarItems('Data', [
        { id: 'lockSelection', text: `🔒 Lock Selection` },
        { id: 'unlockSelection', text: `🔓 Unlock Selection` },
      ]);
    } catch (_) {}
    setHasUnsavedChanges(true);
    autosave.onContentChange();
  }, [stateMachine.state, autosave]);
  handleLockSelectionRef.current = handleLockSelection;

  const activeLockCount = useMemo(() => {
    const spreadsheet = spreadsheetRef.current;
    const sheet       = ((spreadsheet?.sheets ?? [])[activeSheetIdx] ?? {}) as any;
    const sheetName: string = sheet.name ?? `Sheet${activeSheetIdx + 1}`;

    const latestState = new Map<string, boolean>();
    for (const op of lockedRangesRef.current) {
      if (op.sheetName === sheetName || op.sheetIndex === activeSheetIdx) {
        latestState.set(op.range, op.locked);
      }
    }
    let count = 0;
    latestState.forEach(locked => { if (locked) count++; });
    return count;
  }, [lockVersion, activeSheetIdx]);

  const handleUnlockSelection = useCallback(() => {
    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet || stateMachine.state !== 'ACTIVE') return;

    if (documentTypeRef.current !== 'regular') {
      const label = documentTypeRef.current === 'master' ? 'master copy' : 'control copy';
      pendingAlertMessageRef.current =
        `Cell locking is managed on the ${label}. Individual lock/unlock changes cannot be made here.`;
      try { (spreadsheet as any).notify('editAlert', {}); } catch (_) {}
      return;
    }

    const idx       = (spreadsheet as any).activeSheetIndex ?? 0;
    const sheet     = (spreadsheet.sheets?.[idx] ?? {}) as any;
    const selRange  = (sheet.selectedRange ?? '') as string;
    if (!selRange) return;

    const sheetName: string = sheet.name ?? `Sheet${idx + 1}`;
    lockedRangesRef.current = [
      ...lockedRangesRef.current,
      { sheetIndex: idx, sheetName, range: selRange, locked: false },
    ];

    try {
      (spreadsheet as any).cellFormat({ backgroundColor: '' }, `${sheetName}!${selRange}`);
    } catch (_) {}

    setLockVersion(v => v + 1);
    try {
      const count = activeLockCount; // will recalculate on next render
      (spreadsheetRef.current as any)?.updateToolbarItems('Data', [
        { id: 'lockSelection', text: `🔒 Lock Selection` },
        { id: 'unlockSelection', text: `🔓 Unlock Selection` },
      ]);
    } catch (_) {}
    setHasUnsavedChanges(true);
    autosave.onContentChange();
  }, [stateMachine.state, autosave]);
  handleUnlockSelectionRef.current = handleUnlockSelection;

  const replayTints = useCallback((spreadsheet: SpreadsheetComponent) => {
    // Deduplicate by exact range key, keeping each range's LAST occurrence.
    // Then sort by that last-occurrence index so operations are applied in the
    // same chronological order they were originally performed.
    // This is critical for overlapping ranges: e.g. lock A1:C5, unlock B2:C3 —
    // A1:C5 must be applied (yellow) before B2:C3 is cleared, regardless of
    // which range was first touched across sessions.
    const latestPerRange = new Map<string, { op: LockOperation; lastIdx: number }>();
    lockedRangesRef.current.forEach((op, idx) => {
      const key = `${op.sheetIndex}:${op.range}`;
      latestPerRange.set(key, { op, lastIdx: idx });
    });

    const sorted = Array.from(latestPerRange.values())
      .sort((a, b) => a.lastIdx - b.lastIdx);

    for (const { op } of sorted) {
      const sheets = spreadsheet.sheets ?? [];
      if (op.sheetIndex >= sheets.length) continue;
      const sheet = (sheets[op.sheetIndex] ?? {}) as any;
      const sheetName: string = sheet.name ?? `Sheet${op.sheetIndex + 1}`;
      try {
        (spreadsheet as any).cellFormat(
          { backgroundColor: op.locked ? LOCK_TINT_COLOR : '' },
          `${sheetName}!${op.range}`,
        );
      } catch (_) {}
    }
  }, []);

  const applyControlCopySheetProtection = useCallback((
    spreadsheet: SpreadsheetComponent,
    sheetIndex: number,
  ) => {
    const sheet     = (spreadsheet.sheets?.[sheetIndex] ?? {}) as any;
    const sheetName: string = sheet.name ?? `Sheet${sheetIndex + 1}`;
    const sheetOps  = lockedRangesRef.current.filter(
      op => op.sheetName === sheetName || op.sheetIndex === sheetIndex,
    );

    try { (spreadsheet as any).unprotectSheet(sheetIndex, ''); } catch (_) {}

    if (sheetOps.length === 0) return;

    const usedRange = sheet.usedRange;
    const maxRow    = Math.max((usedRange?.rowIndex ?? 0) + 10, 1000);
    const maxCol    = Math.max((usedRange?.colIndex ?? 0) + 10, 100);
    const fullRange = `${sheetName}!A1:${indexToColumn(maxCol)}${maxRow}`;
    try { (spreadsheet as any).lockCells(fullRange, false); } catch (_) {}

    for (const op of sheetOps) {
      try {
        (spreadsheet as any).lockCells(`${sheetName}!${op.range}`, op.locked);
      } catch (_) {}
    }

    spreadsheet.protectSheet(sheetIndex, {
      selectCells:   true,
      formatCells:   false,
      formatColumns: false,
      formatRows:    false,
      insertLink:    false,
    });

  }, [replayTints]);

  const openContent = useCallback((content: string) => {
  if (!content) {
    activateEditor();
    return;
  }

  // Replace your existing try/catch JSON.parse validation with this
  try {
    const parsed = JSON.parse(content);
    lockedRangesRef.current = Array.isArray(parsed?._lockedRanges)
      ? (parsed._lockedRanges as LockOperation[])
      : [];
  } catch {
    stateMachine.onError({
      code: 'DOCUMENT_LOAD_FAILED',
      message: 'Invalid spreadsheet JSON',
      timestamp: Date.now(),
    });
    return;
  }

  // Tell state machine we're loading
  stateMachine.onLoadDocument();

  // Use Syncfusion openComplete event to finalize loading deterministically.
  spreadsheetRef.current?.openFromJson({ file: content, triggerEvent: true });
}, [activateEditor, stateMachine]);

  // ── Communication ──────────────────────────────────────────────────────────
  const communication = useEditorCommunication(COMM_CONFIG);

  // ── Toast notifications ────────────────────────────────────────────────────
  const { showWarning } = useToast();

  // ── API-driven initialization (shared hook handles init + reinit) ──────────

  const buildSpreadsheetConfig = useCallback((data: EditorInitData): EditorConfig => {
    const canEditValue = data.permissions.save && !data.lockInfo.isLocked;
    return {
      componentType: 'spreadsheet',
      documentType: data.documentType,
      canEdit: canEditValue,
      permissions: {
        save: data.permissions.save && !data.lockInfo.isLocked,
        saveAs: data.permissions.saveAs,
        export: data.permissions.export,
        print: data.permissions.print,
      },
      comments: {
        currentUser: data.currentUser?._id && data.currentUser?.name
          ? { id: data.currentUser._id, name: data.currentUser.name, email: data.currentUser.email || '' }
          : { id: '', name: '', email: '' },
        mentionableUsers: [],
      },
      metadata: {
        name: data.version.fullName || data.version.name,
        version: `v${data.version.versionNumber}`,
        id: data.version._id,
      },
      autosave: canEditValue
        ? { enabled: true, debounceMs: 3000, maxIntervalMs: 30000 }
        : undefined,
      allowVersionCreationDisabled: data.allowVersionCreationDisabled,
    };
  }, []);

  const { versionIdRef, initDataRef } = useEditorInit({
    communication,
    stateMachine,
    autosave,
    fetchInitData: fetchSpreadsheetEditorInit,
    buildConfig: buildSpreadsheetConfig,
    callbacks: {
      onBeforeReinit: () => {
        // Reset spreadsheet-specific state for the new version
        // Add this one line inside your existing onBeforeReinit
        lockedRangesRef.current = [];
        setHasUnsavedChanges(false);
        hiddenSheetsRef.current.clear();
        hiddenSheetNameRef.current.clear();
        pendingContentRef.current = null;
      },
      onInitData: (data, _isReinit) => {
        const canEditValue = data.permissions.save && !data.lockInfo.isLocked;
        canEditRef.current = canEditValue;
        documentTypeRef.current = data.documentType;
        setCanEdit(canEditValue);
        setDocumentType(data.documentType);
        setDocumentName(data.version.fullName || data.version.name || 'Workbook');
        setLockReason(data.lockInfo.lockReason);

        permissionsRef.current = {
          save: data.permissions.save && !data.lockInfo.isLocked,
          saveAs: data.permissions.saveAs,
          export: data.permissions.export,
          print: data.permissions.print,
        };
        allowVersionCreationDisabledRef.current = data.allowVersionCreationDisabled ?? false;

        // Enqueue content for when `created` fires (or flush immediately if already created)
        const content = data.version.data || data.version.sfdtData || '';
        pendingContentRef.current = content;
      },
      onConfigDispatched: (_editorConfig, _isReinit) => {
        // Dispatch order matters for React's batching:
        // onConfigReceived (in useEditorInit) stays IDLE+config when starting from IDLE.
        // We must fire onEditorCreated FIRST so the batch processes:
        //   CONFIG_RECEIVED(IDLE) → IDLE+config
        //   EDITOR_CREATED(IDLE+config) → CONFIGURING
        //   CONFIG_APPLIED(CONFIGURING) → READY   ← only valid from CONFIGURING
        // Reverse order (applied first) would fail: CONFIG_APPLIED(IDLE) is invalid.
        stateMachine.onEditorCreated();
        stateMachine.onConfigApplied();

        // If spreadsheet was already created, flush content immediately
        if (isCreatedRef.current && pendingContentRef.current !== null) {
          const content = pendingContentRef.current;
          pendingContentRef.current = null;
          openContent(content);
        }
      },
    },
  });

  // Control-copy path for spreadsheet: CONFIGURING -> READY -> LOADING_DOCUMENT -> APPLYING_PROTECTION -> READY -> ACTIVE
  // When Syncfusion finishes rendering, openContent() calls onDocumentLoaded().
  // For control copies, this transitions LOADING_DOCUMENT -> APPLYING_PROTECTION.
  // Watch for that state and complete the transition: APPLYING_PROTECTION -> READY -> ACTIVE
  useEffect(() => {
    if (stateMachine.state !== 'APPLYING_PROTECTION') return;
    activateAfterProtectionRef.current = true;
    stateMachine.onProtectionApplied();
  }, [stateMachine.state]);

  useEffect(() => {
    if (stateMachine.state !== 'READY') return;
    if (!activateAfterProtectionRef.current) return;

    activateAfterProtectionRef.current = false;
    stateMachine.onActivateEditor();
  }, [stateMachine.state]);

  const handleCellEditing = useCallback((args: any) => {
    if (lockedRangesRef.current.length === 0) return;

    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet) return;

    const idx = (spreadsheet as any).activeSheetIndex ?? 0;
    const sheet = (spreadsheet.sheets?.[idx] ?? {}) as any;
    const sheetName: string = sheet.name ?? `Sheet${idx + 1}`;
    const address: string = args?.address ?? '';
    if (!address) return;

    let isLocked = false;
    for (const op of lockedRangesRef.current) {
      if (op.sheetName !== sheetName && op.sheetIndex !== idx) continue;
      if (isRangeOverlapping(address, op.range)) {
        isLocked = op.locked;
      }
    }

    if (isLocked) {
      args.cancel = true;
      // Trigger Syncfusion's EditAlertDialog — handleDialogBeforeOpen customizes the message
      setTimeout(() => {
        try {
          (spreadsheet as any).notify('editAlert', {});
        } catch (_) {}
      }, 0);
    }
  }, []);

  // ── Menu items ─────────────────────────────────────────────────────────────
  const configureFileMenu = useCallback(() => {
    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet) return;

    // Register once per Spreadsheet instance.
    if (fileMenuConfiguredRef.current) return;

    // Add custom actions once. Visibility is controlled on every menu open.
    try {
      spreadsheet.addFileMenuItems(
        [
          { text: 'Save', id: 'save', iconCss: 'e-icons e-save' },
          { text: 'Save As New Version', id: 'save_as', iconCss: 'e-icons e-save' },
          {
            text: 'Export', id: 'export', iconCss: 'e-icons e-export',
            items: [
              { text: 'Export as Excel', id: 'export_excel', iconCss: 'e-icons e-export' },
              { text: 'Export as PDF',   id: 'export_pdf',   iconCss: 'e-icons e-export' },
              { text: 'Export as CSV',   id: 'export_csv',   iconCss: 'e-icons e-export' },
            ],
          },
        ],
        'Print',
        true
      );
    } catch (_) {}

    fileMenuConfiguredRef.current = true;
  }, []);

  const syncToolbarVisibility = useCallback(() => {
    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet) return;
    // Show for all editable doc types — non-regular docs show an info message on click
    const shouldShow = stateMachine.state === 'ACTIVE' && canEditRef.current;
    try {
      (spreadsheet as any).hideToolbarItems('Data', ['lockSelection', 'unlockSelection'], !shouldShow);
    } catch (_) {}
  }, []);

  const syncFileMenuVisibility = useCallback(() => {
    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet) return;

    // Hide built-ins we do not support in this integration.
    try {
      (spreadsheet as any).hideFileMenuItems(['New', 'Save As', 'Save As\r\n'], true, false);
    } catch (_) {}

    try {
      (spreadsheet as any).hideFileMenuItems(['export'], !permissionsRef.current.export, true);
    } catch (_) {}

    // Open should always be available.
    try {
      (spreadsheet as any).enableFileMenuItems(['Open'], true);
    } catch (_) {}

    // Toggle custom items based on the current version's resolved permissions.
    try {
      (spreadsheet as any).hideFileMenuItems(['save'], !permissionsRef.current.save, true);
    } catch (_) {}
    try {
      // Hide save_as if permission denied OR if lifecycle template disallows version creation
      const shouldHideSaveAs = !permissionsRef.current.saveAs ;
      (spreadsheet as any).hideFileMenuItems(['save_as'], shouldHideSaveAs, true);
    } catch (_) {}
  }, []);

  // ── Created — single point of truth for loading, mirrors TextEditor ────────
  const handleSpreadsheetCreated = useCallback(() => {
    isCreatedRef.current = true;
    fileMenuConfiguredRef.current = false;
    configureFileMenu();
    const spreadsheet = spreadsheetRef.current;
    if (spreadsheet) {
      try {
        (spreadsheet as any).addToolbarItems(
          'Data',
          [
            { type: 'Separator' },
            {
              type: 'Button',
              text: '🔒 Lock Selection',
              id: 'lockSelection',
              tooltipText: 'Lock selected cells',
              click: () => handleLockSelectionRef.current(),
            },
            {
              type: 'Button',
              text: '🔓 Unlock Selection',
              id: 'unlockSelection',
              tooltipText: 'Unlock selected cells',
              click: () => handleUnlockSelectionRef.current(),
            },
          ],
          10
        );
      } catch (_) {}
    }
    syncFileMenuVisibility();

    if (spreadsheet) {
      hiddenSheetsRef.current.clear();
      hiddenSheetNameRef.current.clear();
      const sheets = spreadsheet.sheets || [];
      prevSheetCountRef.current = sheets.length;
      sheets.forEach((sheet: any, index: number) => {
        if ((sheet as any).visible === false) {
          hiddenSheetsRef.current.add(index);
          if ((sheet as any).name) {
            hiddenSheetNameRef.current.add((sheet as any).name);
          }
        }
      });
      (spreadsheet as any).on('activeSheetChanged', (args: any) => {
        setActiveSheetIdx(args.activeSheetIndex ?? 0);
        setLockVersion(v => v + 1);
      });
      console.debug('[SpreadsheetEditor] Initial hidden sheets:', Array.from(hiddenSheetNameRef.current));
    }

    // Flush queued content — same as TextEditor loading doc in created handler
    if (pendingContentRef.current !== null) {
      const content = pendingContentRef.current;
      pendingContentRef.current = null;
      openContent(content);
    }
  }, [configureFileMenu, syncFileMenuVisibility, openContent]);

  const handleActionBegin = useCallback((args: any) => {
    if (args.action === 'protectSheet') {
      args.cancel = true;
      return;
    }

    // Control copies use sheet protection internally to enforce cell-level locks.
    // Prevent users from bypassing it via the ribbon "Unprotect Sheet" button.
    if (args.action === 'unprotectSheet' && documentTypeRef.current === 'control') {
      args.cancel = true;
      return;
    }

    if (args.action === 'cellEdit' || args.action === 'edit') {
      const spreadsheet = spreadsheetRef.current;
      if (spreadsheet && lockedRangesRef.current.length > 0) {
        const idx       = (spreadsheet as any).activeSheetIndex ?? 0;
        const sheet     = (spreadsheet.sheets?.[idx] ?? {}) as any;
        const sheetName: string = sheet.name ?? `Sheet${idx + 1}`;

        // activeCell is always set when editing begins — most reliable source
        const address: string =
          args?.address ??
          args?.eventArgs?.address ??
          sheet.activeCell ??
          sheet.selectedRange ??
          '';

        if (address) {
          // Iterate the full log in order — the LAST overlapping operation wins.
          // A map-based dedup would lose chronological order for overlapping ranges
          // (e.g. lock A1:C5 then unlock B2:C3: clicking B2 must be allowed).
          let isLocked = false;
          for (const op of lockedRangesRef.current) {
            if (op.sheetName !== sheetName && op.sheetIndex !== idx) continue;
            if (isRangeOverlapping(address, op.range)) {
              isLocked = op.locked;
            }
          }
          if (isLocked) {
            args.cancel = true;
            return;
          }
        }
      }
    }

    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet) return;

    if (args.action === 'removeSheet') {
      const sheets = spreadsheet.sheets || [];
      const visibleSheetIndex = sheets.findIndex(
        (s: any) => !hiddenSheetNameRef.current.has(s.name)
      );
      if (visibleSheetIndex >= 0) {
        try { (spreadsheet as any).activeSheetIndex = visibleSheetIndex; } catch (_) {}
      }
      hiddenSheetNameRef.current.forEach((sheetName) => {
        const index = sheets.findIndex((s: any) => s.name === sheetName);
        if (index >= 0) (sheets[index] as any).visible = false;
      });
    }
  }, []);

  // ── Action handler ─────────────────────────────────────────────────────────
  const handleActionComplete = useCallback((args: any) => {
    // Skip all side-effects while we are programmatically re-applying protection
    if (isReprotectingRef.current) return;

    // When a sheet is duplicated, copy the source sheet's lock operations to the
    // new sheet and replay tints so colours and functional locks are both correct.
    if (args?.action === 'duplicateSheet') {
      const spreadsheet = spreadsheetRef.current;
      if (spreadsheet) {
        const sheets = spreadsheet.sheets || [];
        // The active sheet after duplication is the new (duplicate) sheet
        const newIdx      = (spreadsheet as any).activeSheetIndex ?? 0;
        const newSheet    = (sheets[newIdx] ?? {}) as any;
        const newName: string = newSheet.name ?? `Sheet${newIdx + 1}`;

        // Find the source: Syncfusion names the duplicate "Copy of <original>"
        // and the source sheet is identified by stripping that prefix — but it's
        // safer to find the sheet whose lock ops we should clone by matching the
        // "Copy of …" pattern against existing sheet names.
        const sourceNameFromNew = newName.startsWith('Copy of ')
          ? newName.slice('Copy of '.length)
          : null;
        const sourceIdx = sourceNameFromNew !== null
          ? sheets.findIndex((s: any) => s.name === sourceNameFromNew)
          : newIdx - 1;

        if (sourceIdx >= 0) {
          const sourceSheet = (sheets[sourceIdx] ?? {}) as any;
          const sourceName: string = sourceSheet.name ?? `Sheet${sourceIdx + 1}`;

          // Clone source lock operations, re-stamped with the new sheet identity
          const cloned = lockedRangesRef.current
            .filter(op => op.sheetName === sourceName || op.sheetIndex === sourceIdx)
            .map(op => ({ ...op, sheetIndex: newIdx, sheetName: newName }));

          if (cloned.length > 0) {
            lockedRangesRef.current = [...lockedRangesRef.current, ...cloned];
            // Replay tints on the new sheet only
            setTimeout(() => {
              const sp = spreadsheetRef.current;
              if (!sp) return;
              for (const op of cloned) {
                try {
                  (sp as any).cellFormat(
                    { backgroundColor: op.locked ? LOCK_TINT_COLOR : '' },
                    `${newName}!${op.range}`,
                  );
                } catch (_) {}
              }
            }, 100);
          }
        }
        prevSheetCountRef.current = sheets.length;
      }
      setHasUnsavedChanges(true);
      autosave.onContentChange();
      return;
    }

    // If the user managed to unprotect a sheet via the ribbon Data tab, re-apply
    // protection immediately.  Programmatic protectSheet() calls do NOT fire
    // actionComplete (no triggerEvent), so this handler won't loop.
    if (args?.action === 'protectSheet' && args?.eventArgs?.isProtected === false) {
      const spreadsheet = spreadsheetRef.current;
      if (spreadsheet) {
        const sheetIndex = typeof args.eventArgs?.sheetIndex === 'number'
          ? args.eventArgs.sheetIndex
          : ((spreadsheet as any).activeSheetIndex ?? 0);

        // Only force-reprotect for docs where the sheet must always stay protected.
        // Regular editable docs with locked ranges: cell-level locks are enforced
        // by handleActionBegin (cellEdit), so Syncfusion sheet protection can toggle freely.
        const shouldBeProtected =
          !canEditRef.current ||
          documentTypeRef.current === 'control';

        if (shouldBeProtected) {
          isReprotectingRef.current = true;
          try {
            if (!canEditRef.current) {
              spreadsheet.protectSheet(sheetIndex, {
                selectCells: true, formatCells: false,
                formatColumns: false, formatRows: false, insertLink: false,
              });
            } else {
              applyControlCopySheetProtection(spreadsheet, sheetIndex);
            }
          } finally {
            isReprotectingRef.current = false;
          }
          return; // Don't mark dirty — this was a forced re-protection
        }
        // Regular editable doc: user voluntarily unprotected → mark dirty
        if (canEdit && stateMachine.state === 'ACTIVE') {
          setHasUnsavedChanges(true);
          autosave.onContentChange();
        }
      }
      return;
    }

    if (!canEdit || stateMachine.state !== 'ACTIVE') return;
    
    const spreadsheet = spreadsheetRef.current;
    if (spreadsheet) {
      const sheets = spreadsheet.sheets || [];
      const currentSheetCount = sheets.length;
      
      if (currentSheetCount < prevSheetCountRef.current) {
        hiddenSheetNameRef.current.forEach((sheetName) => {
          const sheetIndex = sheets.findIndex((s: any) => s.name === sheetName);
          if (sheetIndex >= 0) {
            (spreadsheet as any).hideSheet(sheetIndex);
          }
        });
        try {
          (spreadsheet as any).notify('refreshSheetTabs', {});
        } catch (err) {
        }
      }
      
      prevSheetCountRef.current = currentSheetCount;
      
      hiddenSheetsRef.current.clear();
      hiddenSheetNameRef.current.clear();
      sheets.forEach((sheet: any, index: number) => {
        if ((sheet as any).visible === false) {
          hiddenSheetsRef.current.add(index);
          if ((sheet as any).name) {
            hiddenSheetNameRef.current.add((sheet as any).name);
          }
        }
      });
    }
    
    setHasUnsavedChanges(true);
    autosave.onContentChange();
  }, [canEdit, stateMachine.state, applyControlCopySheetProtection]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
      const spreadsheet = spreadsheetRef.current;
      if (spreadsheet && stateMachine.state === 'ACTIVE') {
        e.preventDefault();
        e.stopPropagation();
        try {
          if (typeof (spreadsheet as any).selectAll === 'function') {
            (spreadsheet as any).selectAll();
          } else {
       
            const sheet = spreadsheet.sheets?.[spreadsheet.activeSheetIndex ?? 0];
            if (sheet) {
              const maxRow = Math.max(sheet.usedRange?.rowIndex ?? 100, 50);
              const maxCol = Math.max(sheet.usedRange?.colIndex ?? 20, 10);
              const colLetter = maxCol > 0 ? String.fromCharCode(64 + Math.min(maxCol + 1, 26)) : 'Z';
              (spreadsheet as any).selectRange(`A1:${colLetter}${maxRow + 1}`);
            }
          }
        } catch (err) {
        }
      }
    }
  }, [stateMachine.state]);

  // ── File menu ──────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!canEditRef.current || !permissionsRef.current.save) return;
    const id = versionIdRef.current;
    if (!id) return;
    autosave.onManualSaveStart();
    try {
      const payload = await buildSavePayload();
      await saveSpreadsheetFileVersion({
        _id: id,
        sfdtData: payload.content.spreadsheetJson || '',
        data: payload.content.spreadsheetJson || ''
      });
      autosave.onSaveComplete();
      setHasUnsavedChanges(false);
      console.log('[SpreadsheetEditor] Save completed');
    } catch (err) {
      console.error('[SpreadsheetEditor] Save failed:', err);
      autosave.onSaveFailed((err as Error).message || 'Save failed');
    }
  }, [buildSavePayload, autosave]);

  const handleSaveAs = useCallback(async () => {
    if (!permissionsRef.current.saveAs) return;

    // Check if version creation is disabled by lifecycle template
    const allowVersionCreationDisabled = initDataRef.current?.allowVersionCreationDisabled ?? false;

    if (allowVersionCreationDisabled) {
      console.warn('[SpreadsheetEditor] Save As disabled: lifecycle template does not allow version creation in current state');
      showWarning('Version cannot be created because the current status does not allow version creation.');
      return;
    }

    // Check if entity is under state transition (approval pending)
    const isUnderTransition = initDataRef.current?.version?.lifecycleProperties?.isUnderTransition ?? false;
    console.log('[SpreadsheetEditor] Save As - isUnderTransition check:', {
      isUnderTransition,
      lifecycleProperties: initDataRef.current?.version?.lifecycleProperties,
    });

    if (isUnderTransition) {
      console.warn('[SpreadsheetEditor] Save As disabled: entity is under status transition');
      showWarning('Cannot create new version while entity is under status transition. Please wait for the current transition to complete.');
      return;
    }

    // Show the reason modal instead of directly calling API
    setIsReasonModalOpen(true);
  }, [showWarning]);

  const handleReasonModalConfirm = useCallback(async (reason: string) => {
    setIsReasonModalOpen(false);
    const id = versionIdRef.current;
    if (!id) return;
    autosave.onManualSaveStart();
    try {
      const payload = await buildSavePayload();
      const newVersion = await saveAsSpreadsheetFileVersion({
        fromVersionId: id,
        sfdtData: payload.content.spreadsheetJson || '',
        data: payload.content.spreadsheetJson || '',
        versionReason: reason,
      });
      versionIdRef.current = newVersion._id;
      autosave.onSaveAsComplete();
      setHasUnsavedChanges(false);
      console.log('[SpreadsheetEditor] Save As completed, new version:', newVersion._id);
      // Navigate to the new version in Angular
      communication.sendNavigate('SPREADSHEETFILE_VERSION', newVersion._id, newVersion.fullName);
    } catch (err) {
      console.error('[SpreadsheetEditor] Save As failed:', err);
      autosave.onSaveFailed((err as Error).message || 'Save As failed');
    }
  }, [buildSavePayload, autosave, communication]);

  const handleReasonModalCancel = useCallback(() => {
    setIsReasonModalOpen(false);
    setPendingVersionReason(null);
  }, []);

  const handleExport = useCallback(async (format: 'excel' | 'pdf' | 'csv') => {
    if (!permissionsRef.current.export) return;
    const id = versionIdRef.current;
    if (!id) return;

    const payload = await buildSavePayload();
    const fileName = payload.metadata.documentName || 'Workbook';

    try {
      if (format === 'excel') {
        const res = await exportSpreadsheetAsExcel(id);
        if (!res?.base64) return;
        const bytes = Uint8Array.from(atob(res.base64), c => c.charCodeAt(0));
        downloadBlob(
          new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          `${fileName}.xlsx`
        );
      }

      if (format === 'pdf') {
        const res = await exportSpreadsheetAsPDF({
          versionId: id,
          spreadsheetJson: payload.content.spreadsheetJson || '',
          fileName,
        });
        const pdfContent = res?.pdfContent || res?.data || res?.content;
        if (!pdfContent) return;
        const bytes = Uint8Array.from(atob(pdfContent), c => c.charCodeAt(0));
        downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `${fileName}.pdf`);
      }

      if (format === 'csv') {
        const csv = convertSpreadsheetToCSV(payload.content.spreadsheetJson || '{}');
        if (!csv) return;
        downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${fileName}.csv`);
      }
    } catch (err) {
      console.error(`[SpreadsheetEditor] ${format} export failed:`, err);
    }
  }, [buildSavePayload, versionIdRef]);

  function downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Dialog intercept ───────────────────────────────────────────────────────
  const handleDialogBeforeOpen = useCallback((args: any) => {
    // Intercept Syncfusion's unprotect password dialog.
    // After openFromJson, every protected sheet has isImportProtected=true which
    // forces Syncfusion to always show the password dialog — even for sheets that
    // had no password. We catch it here, cancel for no-password sheets, and call
    // unprotectSheet('') directly. Sheets with a real password let the dialog run.
    if (args?.dialogName === 'UnProtectSheet') {
      const spreadsheet = spreadsheetRef.current;
      if (spreadsheet && canEditRef.current) {
        // Block unprotect for control copies — sheet protection is our internal
        // cell-lock enforcement mechanism, not a user-facing feature.
        if (documentTypeRef.current === 'control') {
          args.cancel = true;
          return;
        }
        const sheetIdx = (spreadsheet as any).activeSheetIndex ?? 0;
        const sheet = (spreadsheet.sheets?.[sheetIdx] ?? {}) as any;
        const hasPassword = typeof sheet.password === 'string' && sheet.password.length > 0;
        if (!hasPassword) {
          args.cancel = true;
          try { (spreadsheet as any).unprotectSheet(sheetIdx, ''); } catch (_) {}
          setHasUnsavedChanges(true);
        }
      }
      return;
    }

    if (args?.dialogName === 'EditAlertDialog') {
      // Pending message set by Lock/Unlock toolbar buttons on non-regular docs
      if (pendingAlertMessageRef.current) {
        args.content = pendingAlertMessageRef.current;
        pendingAlertMessageRef.current = null;
        return;
      }
      if (!canEditRef.current) {
        // Whole document is read-only
        args.content = lockReason ||
          'This spreadsheet is read-only. You do not have permission to edit it.';
      } else {
        // Check whether the sheet itself is protected via Syncfusion sheet protection
        const spreadsheet = spreadsheetRef.current;
        const sheetIdx = (spreadsheet as any)?.activeSheetIndex ?? 0;
        const sheet = (spreadsheet?.sheets?.[sheetIdx] ?? {}) as any;
        // For control copies, sheet protection is our internal lock-enforcement
        // mechanism — never show "sheet is protected" to users; always explain
        // it as a cell-level lock.
        const isSheetProtected =
          sheet.isProtected === true && documentTypeRef.current !== 'control';

        if (isSheetProtected) {
          args.content =
            'This sheet is protected. Click "Unprotect Sheet" in the Data tab to enable editing.';
        } else {
          // A specific cell range has been locked via the Lock Selection toolbar
          args.content =
            'This cell is locked. To edit it, select the range and click ' +
            '"🔓 Unlock Selection" in the toolbar above.';
        }
      }
    }
  }, [lockReason]);

  // ── Loading message ────────────────────────────────────────────────────────
  const loadingMessage = useMemo(() => {
    switch (stateMachine.state) {
      case 'IDLE':
      case 'WAITING_FOR_CONFIG': return 'Waiting for configuration...';
      case 'CONFIGURING':
      case 'READY':
      case 'LOADING_DOCUMENT': return 'Loading spreadsheet...';
      case 'ERROR':            return 'An error occurred. Please refresh the page.';
      default:                 return '';
    }
  }, [stateMachine.state]);
  const handleOpenComplete = useCallback(() => {
    activateEditor();
  }, [activateEditor]);

  React.useEffect(() => {
    const spreadsheetElement = spreadsheetRef.current?.element;
    if (spreadsheetElement) {
      spreadsheetElement.addEventListener('keydown', handleKeyDown as EventListener, true);
      return () => {
        spreadsheetElement.removeEventListener('keydown', handleKeyDown as EventListener, true);
      };
    }
  }, [handleKeyDown]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const isActive = stateMachine.state === 'ACTIVE';
  const isError = stateMachine.state === 'ERROR';
  const shouldMountEditor = stateMachine.config !== null;
  const showLockToolbar = isActive && canEdit && documentType !== 'control';

  return (
    <div className={styles.container}>
      {/* Toolbar overlays: Document type badge + Autosave indicator */}
      <div className={styles.toolbarOverlay}>
        <DocumentTypeBadge documentType={documentType} />
        {stateMachine.config?.autosave?.enabled && canEdit && (
          <AutosaveIndicator
            status={autosave.state.status}
            lastSavedAt={autosave.state.lastSavedAt}
            error={autosave.state.error}
          />
        )}
      </div>

      {/* Read-only indicator banner */}
      {isActive && !canEdit && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          padding: '12px 16px',
          margin: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          color: '#856404',
        }}>
          <span style={{ fontSize: '18px' }}>🔒</span>
          <span>{lockReason || 'This spreadsheet is read-only. You do not have permission to edit it.'}</span>
        </div>
      )}

      {!isActive && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            {!isError && <div className={styles.spinner} />}
            <p>{loadingMessage}</p>
          </div>
        </div>
      )}

    

      {/* Mount as soon as config arrives — mirrors TextEditor's {stateMachine.config && ...} */}
      {shouldMountEditor && (
        <div
          className={styles.editorContainer}
          style={{
            visibility: isActive ? 'visible' : 'hidden',
            top: 0,
          }}
          onClick={(e: React.MouseEvent) => {
            const popup = (spreadsheetRef.current as any)?.ribbon?.fileMenuPopup;
            if (
              (e.target as HTMLElement).closest('.e-file-btn') &&
              popup?.element?.classList.contains('e-popup-open')
            ) {
              popup.hide();
            }
          }}
        >
         <SpreadsheetComponent
  ref={spreadsheetRef}
  showRibbon={showRibbon}
  showFormulaBar={showFormulaBar}
  showSheetTabs={true}
  allowOpen={true}
  allowSave={true}
  openUrl={environment.getSyncfusionServiceUrl() + '/open'}
  created={handleSpreadsheetCreated}
  openComplete={handleOpenComplete}
  fileMenuBeforeOpen={syncFileMenuVisibility}
  actionComplete={handleActionComplete}
  actionBegin={handleActionBegin}
  cellEdit={handleCellEditing}
  height="100%"
  width="100%"
  contextMenuBeforeOpen={handleContextMenuBeforeOpen}
  dialogBeforeOpen={handleDialogBeforeOpen}
  fileMenuItemSelect={async (args: any) => {
    const id = args?.item?.id as string | undefined;
    if (id === 'save')    await handleSave();
    if (id === 'save_as') await handleSaveAs();
    if (id === 'export_excel') await handleExport('excel');
    if (id === 'export_pdf')   await handleExport('pdf');
    if (id === 'export_csv')   await handleExport('csv');
  }}
>
  {/* No SheetsDirective — sheets come from openFromJson, 
      declarative sheet definitions overwrite imperatively loaded data */}
</SpreadsheetComponent>
        </div>
      )}

      {/* Version Reason Modal */}
      <VersionReasonModal
        isOpen={isReasonModalOpen}
        title="Create New Version"
        placeholder="Enter reason for creating a new version..."
        confirmBtnText="Save Version"
        cancelBtnText="Cancel"
        onConfirm={handleReasonModalConfirm}
        onCancel={handleReasonModalCancel}
      />
    </div>
  );
};
function parseCellRef(ref: string): { row: number; col: number } {
  const clean = ref.includes('!') ? ref.split('!')[1] : ref;
  const col = clean.match(/[A-Z]+/)?.[0] ?? 'A';
  const row = clean.match(/\d+/)?.[0] ?? '1';
  let colIdx = 0;
  for (let i = 0; i < col.length; i++) {
    colIdx = colIdx * 26 + col.charCodeAt(i) - 64;
  }
  return { row: parseInt(row) - 1, col: colIdx - 1 };
}

function isRangeOverlapping(address: string, range: string): boolean {
  const addrParts = (address.includes('!') ? address.split('!')[1] : address).split(':');
  const rngParts  = (range.includes('!') ? range.split('!')[1] : range).split(':');
  const aStart = parseCellRef(addrParts[0]);
  const aEnd   = parseCellRef(addrParts[1] ?? addrParts[0]);
  const rStart = parseCellRef(rngParts[0]);
  const rEnd   = parseCellRef(rngParts[1] ?? rngParts[0]);
  return (
    aStart.col <= rEnd.col && aEnd.col >= rStart.col &&
    aStart.row <= rEnd.row && aEnd.row >= rStart.row
  );
}

export default SpreadsheetEditor;